import React, { useEffect, useRef, useState } from 'react';
import { SECTIONS, MAX_CAM, THRESHOLD_DEPTH, scrollState, clamp, smoothstep } from '@/lib/depth';
import { HeroContent } from '@/components/sections/HeroContent';
import { SocialCard } from '@/components/sections/SocialCard';
import { LinksCard } from '@/components/sections/LinksCard';
import { ThresholdLine } from '@/components/sections/ThresholdLine';
import { KingBelowCard } from '@/components/sections/KingBelowCard';
import { LettersCard } from '@/components/sections/LettersCard';
import { EndNote } from '@/components/sections/EndNote';

// About + Partnerships are rendered as fixed overlays (AboutOverlay /
// PartnershipsOverlay) composed around hero2/hero3 — not as depth cards.
// 'work' stays in the depth map purely as a navigation target and renders empty.
const Empty = () => null;

const COMPONENTS = {
  hero: HeroContent,
  work: Empty,
  social: SocialCard,
  links: LinksCard,
  threshold: ThresholdLine,
  tkb: KingBelowCard,
  letters: LettersCard,
  end: EndNote,
};

/**
 * The Z-axis depth engine. The page scroll position is the camera's position
 * along the Z axis. Every frame each section layer is translated in 3D so it
 * rushes past the camera, with opacity ramps for entering/leaving range.
 */
export default function DepthExperience() {
  const refs = useRef({});
  const activeRef = useRef({});
  const [activeMap, setActiveMap] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
    scrollState.camera = 0;
    scrollState.blend = 0;
    let raf;

    const tick = () => {
      const target = window.scrollY || window.pageYOffset || 0;
      scrollState.camera += (target - scrollState.camera) * 0.09;
      if (Math.abs(target - scrollState.camera) < 0.08) scrollState.camera = target;
      const cam = scrollState.camera;
      scrollState.blend = smoothstep(THRESHOLD_DEPTH - 2000, THRESHOLD_DEPTH + 900, cam);
      scrollState.progress = clamp(cam / MAX_CAM, 0, 1);

      let changed = false;
      SECTIONS.forEach((s) => {
        const el = refs.current[s.id];
        if (!el) return;
        const rel = s.depth - cam;

        let o;
        if (s.threshold) {
          o = clamp(1 - Math.abs(rel) / 800, 0, 1);
        } else {
          const [fiStart, fiEnd] = s.fadeIn || [2200, 1000];
          const fadeInO = s.noFadeIn ? 1 : clamp((fiStart - rel) / (fiStart - fiEnd), 0, 1);
          const fadeOutO = s.noFadeOut ? 1 : clamp((rel + 850) / 600, 0, 1);
          o = Math.min(fadeInO, fadeOutO);
        }

        el.style.opacity = o.toFixed(3);
        el.style.visibility = o < 0.02 ? 'hidden' : 'visible';
        el.style.transform = `translate3d(${s.x}, ${s.y}, ${(cam - s.depth).toFixed(1)}px)${
          s.rotY ? ` rotateY(${s.rotY}deg)` : ''
        }`;
        el.dataset.depthProgress = clamp((2400 - rel) / 1800, 0, 1).toFixed(3);

        const nowActive = activeRef.current[s.id] || rel < (s.activeAt || 2200);
        if (nowActive !== !!activeRef.current[s.id]) {
          activeRef.current[s.id] = nowActive;
          changed = true;
        }
      });
      if (changed) setActiveMap({ ...activeRef.current });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <>
      <div
        className="fixed inset-0 z-10 overflow-hidden"
        style={{ perspective: '1200px', perspectiveOrigin: '50% 46%' }}
        data-testid="depth-stage"
      >
        {SECTIONS.map((s, i) => {
          const Comp = COMPONENTS[s.id];
          return (
            <div
              key={s.id}
              ref={(el) => {
                refs.current[s.id] = el;
              }}
              data-depth-host="1"
              data-section={s.id}
              className="pointer-events-none absolute inset-0 flex items-center justify-center"
              style={{
                zIndex: 100 - i,
                willChange: 'transform, opacity',
                backfaceVisibility: 'hidden',
                visibility: s.id === 'hero' ? 'visible' : 'hidden',
                opacity: s.id === 'hero' ? 1 : 0,
              }}
            >
              <div className="pointer-events-auto">
                <Comp active={s.id === 'hero' ? true : !!activeMap[s.id]} />
              </div>
            </div>
          );
        })}
      </div>
      {/* Scroll runway — the camera travels MAX_CAM px of depth */}
      <div style={{ height: `calc(${MAX_CAM}px + 100vh)` }} aria-hidden="true" />
    </>
  );
}
